// angular
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, effect, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { ICardProperty } from '@app/core/components/card-property/card-property.component';
import { CardTitleComponent } from '@app/core/components/card-title/card-title.component';
import { TextSummarizerComponent } from '../text-summarizer/text-summarizer.component';
// services
import { UserService } from '@app/shared/services/user.service';
// types & enums
import { IBusiness } from '@app/+suppliers/shared/interfaces/business.interface';
import { ICardTitle } from '@app/shared/interfaces/card-title.interface';
import { IUser } from '@app/shared/types/user.type';
import { Roles } from '@app/shared/enums/roles.enum';
// pipes
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';

@Component({
  selector: 'pwj-supplier-card',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    CardTitleComponent,
    TranslateModule,
    NgOptimizedImage,
    ImageSourcePipe,
    TranslateDataPipe,
    HasAccessPipe,
    TextSummarizerComponent,
  ],
  templateUrl: './supplier-card.component.html',
  styleUrl: './supplier-card.component.scss',
})
export class SupplierCardComponent {
  public roles = Roles;
  public cardTitleData!: ICardTitle;
  public userData!: IUser;
  // card properties
  public cardProperties!: ICardProperty[];

  // inputs
  supplierCardData = input.required<IBusiness>();

  // dependencies
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _userService = inject(UserService);

  constructor() {
    effect(() => {
      this.setCardTitleData(this.supplierCardData());
      this.setCardProperties(this.supplierCardData());
    });

    effect(() => {
      this.userData = this._userService.userSignal();
    });
  }

  public setCardTitleData(data: IBusiness) {
    this.cardTitleData = {
      title: data?.title || '-',
      titleClasses: '!text-4xl !text-text-dark',
      subTitle: `${data?.owner?.first_name || '-'} ${
        data?.owner?.last_name || '-'
      }`,
      subTitleClasses: '[&>span]:!text-3xl !text-primary-dark !mt-1',
      summarizeTitle: true,
      summarizeTitleLength: 40,
      summarizeSubTitleLength: 40,
      imgClasses: '!w-48 !h-48 rounded-4',
    };
  }

  public setCardProperties(data: IBusiness) {
    this.cardProperties = [
      {
        name: 'industries.phone',
        value: data?.mobile?.mobile_number
          ? `${data?.mobile?.prefix} ${data?.mobile?.mobile_number}`
          : '-',
        valueClasses: this._hasAccessPipe.transform([
          Roles.ViewBusinessContactInfo,
        ])
          ? ''
          : '',
        icon: 'phone',
        summarizeLength: 12,
        propertyWidth: '4em',
      },
      {
        name: 'industries.category',
        value: data?.categories?.length || '-',
        icon: 'box',
      },
      {
        name: 'global.topProducts',
        value: data?.products?.length || '-',
        icon: 'box',
        propertyWidth: '4em',
      },
      {
        name: 'industries.grade',
        value: data?.grade || '-',
        icon: 'box',
        propertyValueIcon: 'star',
        valueIconClasses: 'text-primary-dark !text-lg mx-4 mt-4',
      },
      {
        name: 'industries.address',
        value: data?.address || '-',
        isI18nValueType: true,
        icon: 'location',
        valueClasses: this._hasAccessPipe.transform([
          Roles.ViewBusinessContactInfo,
        ])
          ? ''
          : '',
        iconClasses: '!text-primary-dark',
        summarizeLength: 27,
      },
    ];
  }
}
